import { urlFor } from '@/sanity/lib/image';
// Fallback: loosen the image source type to avoid missing type exports from @sanity/image-url
type SanityImageSource = any;
import { VIDEO_VARIANTS } from '../constants';
import { FinalValues, VideoImageData } from '../interfaces';
export const getFinalValues = (variant: VideoImageData['variant'], block: VideoImageData): FinalValues => {
  const defaultRes = {
    thumbnail: block.thumbnail,
    heading: block.imageHeading ?? '',
    description: block.description,
    buttons: null,
  };
  switch (variant) {
    case VIDEO_VARIANTS.ImageVideoWithText:
      return {
        thumbnail: block.image,
        heading: block.imageHeading ?? '',
        description: block.description,
        buttons: block.buttons ?? null,
        isVideo: block.isVideo,
      };
    case VIDEO_VARIANTS.VideoWithText:
      return {
        thumbnail: block.thumbnail,
        heading: block.textOverlay?.heading ?? '',
        description: block.textOverlay?.description ?? '',
        buttons: null,
      };
    case VIDEO_VARIANTS.VideoWithoutText:
    default:
      return defaultRes;
  }
};

// Vimeo ID extractor
export const getVimeoVideoId = (url: string): string => {
  if (!url) return '';
  if (/^\d+$/.test(url)) return url;
  const match = url.match(/(?:vimeo\.com\/.*?\/)?(\d+)/);
  return match ? match[1] : url;
};

// Duration formatter
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

type ImageBuilder = ReturnType<typeof urlFor>;

const isRemoteUrl = (value: unknown): value is string =>
  typeof value === 'string' && /^(https?:)?\/\//i.test(value);

export const getDirectImageUrl = (src: unknown): string | undefined => {
  if (!src || typeof src !== 'object') return undefined;

  const image = src as Record<string, any>;
  const bynderImage = image.bynderImage;

  if (bynderImage && typeof bynderImage === 'object') {
    return getDirectImageUrl(bynderImage);
  }

  return (
    image.selectedUrl ||
    image.previewUrl ||
    image.datUrl ||
    image.files?.transformBaseUrl?.url ||
    image.files?.webImage?.url ||
    image.previewImg ||
    image.derivatives?.webImage ||
    image.asset?.url ||
    image.url
  );
};

const createRemoteUrlBuilder = (src: string): ImageBuilder => {
  const params = new URLSearchParams();
  const builder: any = {
    width(value: number) {
      params.set('w', String(value));
      return builder;
    },
    height(value: number) {
      params.set('h', String(value));
      return builder;
    },
    quality(value: number) {
      params.set('q', String(value));
      return builder;
    },
    fit(value: string) {
      params.set('fit', value);
      return builder;
    },
    auto(value: string) {
      params.set('auto', value);
      return builder;
    },
    dpr(value: number) {
      if (value !== 1) params.set('dpr', String(value));
      return builder;
    },
    url() {
      try {
        const url = new URL(src.startsWith('//') ? `https:${src}` : src);
        params.forEach((value, key) => url.searchParams.set(key, value));
        return url.toString();
      } catch {
        return src;
      }
    },
  };

  return builder as ImageBuilder;
};

export const getImageBuilder = (src: SanityImageSource): ImageBuilder => {
  if (isRemoteUrl(src)) return createRemoteUrlBuilder(src);
  if (typeof src === 'string') return urlFor(src);

  const directUrl = getDirectImageUrl(src);
  if (isRemoteUrl(directUrl)) return createRemoteUrlBuilder(directUrl);

  if (
    src &&
    typeof src === 'object' &&
    ('bynderImage' in src || 'selectedUrl' in src || 'url' in src)
  ) {
    return urlFor(src);
  }

  if (src && typeof src === 'object' && 'asset' in src && src.asset?._ref) {
    return urlFor(src);
  }

  if (src && typeof src === 'object' && 'url' in src && src.url) {
    return urlFor(src.url);
  }

  return urlFor(src);
};

// Check if the given object is a valid image
export const isValidImage = (image: string | Record<string, Record<string, string> | string>): boolean => {
  return !!(typeof image === 'string' || image?.url || image?.asset);
};
